import React, { useEffect } from 'react';
import { BEHANCE, designs, photos, projects } from '../data';
import Arrow, { BackArrow } from '../components/Arrow';
import './Work.css';

// The two sides of the work, each with its own page
export const sides = {
  engineer: {
    id: 'engineer',
    number: '01',
    title: 'Engineer',
    line: 'Me behind the laptop: web apps, APIs and the systems behind them, built end to end.',
    cta: "See what I've built",
    image: photos.code,
    intro: 'Web apps and websites I have built end to end. They are all live: open any one to read about it.',
    entries: projects.map(p => ({
      key: p.slug,
      name: p.name,
      text: `${p.sub.split(' · ')[0]} · ${p.line}`,
      href: `#/work/${p.slug}`,
      external: false,
    })),
  },
  design: {
    id: 'design',
    number: '02',
    title: 'Design',
    line: 'Brand identities, logos, social, campaign and print design. Tell me the idea; I will design it.',
    cta: 'Step inside',
    image: designs[4].image,
    intro: 'Brand identities, logos and campaign pieces. Click any piece to see the full project on Behance.',
    entries: designs.map(d => ({
      key: d.short,
      name: d.title,
      text: d.blurb,
      image: d.image,
      size: d.size,
      href: d.link || BEHANCE,
      external: true,
    })),
  },
};

export default function Work() {
  // Phones can't hover, so a door comes to colour while it sits in the middle of the screen instead
  useEffect(() => {
    if (!window.matchMedia('(hover: none), (max-width: 760px)').matches) return undefined;
    const io = new IntersectionObserver(
      list => list.forEach(e => e.target.classList.toggle('lit', e.isIntersecting)),
      { rootMargin: '-38% 0px -38% 0px' },
    );
    document.querySelectorAll('.side').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <main className="work shell">
      <div className="meta-row fade-in">
        <span className="meta">Selected work</span>
        <span className="meta">Lagos · Nigeria</span>
      </div>

      <h1 className="display work-title">
        <span className="mask"><span>Work</span></span>
      </h1>

      <div className="sides-head fade-in" style={{ '--d': 4 }}>
        <h2 className="display">Explore my work.</h2>
        <p>Two sides. Pick one.</p>
      </div>

      <div className="sides">
        {Object.values(sides).map((s, i) => (
          <a key={s.id} href={`#/${s.id}`} className="side fade-in" style={{ '--d': 5 + i }}>
            <img src={s.image} alt="" decoding="async" />
            <span className="side-copy">
              <span className="meta side-num">{s.number}</span>
              <span className="display side-title">{s.title}</span>
              <span className="side-line">{s.line}</span>
              <span className="side-cta">{s.cta} <Arrow /></span>
            </span>
          </a>
        ))}
      </div>
    </main>
  );
}

// One side's own page: a numbered list of its work
export function WorkSide({ side }) {
  const s = sides[side];
  const other = sides[side === 'engineer' ? 'design' : 'engineer'];
  return (
    <main className="work shell">
      <div className="meta-row fade-in">
        <a className="meta work-back" href="#/work"><BackArrow /> Work</a>
        <span className="meta">Lagos · Nigeria</span>
      </div>

      <h1 className="display side-page-title">
        <span className="mask"><span>{s.title}</span></span>
      </h1>
      <p className="side-intro fade-in" style={{ '--d': 2 }}>{s.intro}</p>

      {side === 'design' ? <><DesignGallery entries={s.entries} /><BehanceMore /></> : (
      <ol className="work-list">
        {s.entries.map((e, i) => (
          <li key={e.key} className="rv">
            <a href={e.href} {...(e.external ? { target: '_blank', rel: 'noreferrer' } : {})}>
              <span className="meta work-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="work-name">
                <span className="work-name-text">{e.name}</span>
                <span className="work-line">{e.text}</span>
              </span>
              <span className="work-go"><Arrow /></span>
            </a>
          </li>
        ))}
      </ol>
      )}

      <a className="side-next rv" href={`#/${other.id}`}>
        <span className="meta">The other side</span>
        <span className="display">{other.title}</span>
        <Arrow />
      </a>
    </main>
  );
}

// The design side shows the work itself: each piece in a frame cut to its own shape, never cropped,
// opening its project on Behance
function DesignGallery({ entries }) {
  return (
    <ol className="design-grid">
      {entries.map((e, i) => (
        <li key={e.key} className="design-item rv">
          <a className="design-frame" href={e.href} target="_blank" rel="noreferrer" aria-label={`${e.name} on Behance`}>
            <img src={e.image} alt="" width={e.size[0]} height={e.size[1]} loading="lazy" decoding="async" />
          </a>
          <div className="design-info">
            <span className="meta work-num">{String(i + 1).padStart(2, '0')}</span>
            <div>
              <h2 className="design-name">{e.name}</h2>
              <p className="design-text">{e.text}</p>
            </div>
          </div>
          <a className="design-btn" href={e.href} target="_blank" rel="noreferrer">
            View on Behance <Arrow />
          </a>
        </li>
      ))}
    </ol>
  );
}

// The rest of the design work lives on Behance
function BehanceMore() {
  return (
    <div className="design-more rv">
      <p>Like what you see? There’s plenty more where these came from.</p>
      <a className="design-btn design-btn-big" href={BEHANCE} target="_blank" rel="noreferrer">
        View more on Behance <Arrow />
      </a>
    </div>
  );
}
